import React, { useState, useEffect, useCallback } from 'react';
import { LOGO_TALKU } from './logos';

const API = 'https://talku.online';
const SF = "'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const PLAN_COLORS = { trial: '#f59e0b', starter: '#22c55e', pro: '#3b82f6', enterprise: '#8b5cf6' };
const PLAN_BG     = { trial: '#fef3c7', starter: '#dcfce7', pro: '#dbeafe', enterprise: '#ede9fe' };

export default function Admin() {
  const [adminUser, setAdminUser] = useState(() => {
    try { const s = localStorage.getItem('talku_admin'); return s ? JSON.parse(s) : null; } catch { return null; }
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const authH = () => ({ 'Authorization': `Bearer ${adminUser?.token || ''}` });

  const fetchDashboard = useCallback(async () => {
    if (!adminUser) return;
    setLoading(true);
    try {
      const [sr, tr] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers: authH() }),
        fetch(`${API}/admin/tenants`, { headers: authH() }),
      ]);
      if (sr.ok) setStats(await sr.json());
      if (tr.ok) setTenants(await tr.json());
    } catch {}
    setLoading(false);
  // eslint-disable-next-line
  }, [adminUser]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    try {
      const r = await fetch(`${API}/admin/tenants/${id}`, { headers: authH() });
      if (r.ok) setTenantDetail(await r.json());
    } catch {}
    setDetailLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const r = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (r.ok) {
        const d = await r.json();
        const u = { email: d.email, name: d.name, token: d.token };
        localStorage.setItem('talku_admin', JSON.stringify(u));
        setAdminUser(u);
      } else {
        setLoginError('Credenciales incorrectas');
      }
    } catch {
      setLoginError('Error de conexion');
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('talku_admin');
    setAdminUser(null);
    setStats(null);
    setTenants([]);
    setSelectedTenant(null);
    setTenantDetail(null);
  };

  const toggleTenantActive = async (t) => {
    setActionLoading(t.id);
    try {
      const r = await fetch(`${API}/admin/tenants/${t.id}`, {
        method: 'PATCH',
        headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !t.active }),
      });
      if (r.ok) await fetchDashboard();
    } catch {}
    setActionLoading(null);
  };

  const openDetail = (t) => { setSelectedTenant(t.id); fetchDetail(t.id); };
  const closeDetail = () => { setSelectedTenant(null); setTenantDetail(null); };

  const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // ─── LOGIN ───
  if (!adminUser) {
    return (
      <>
        <style>{`
          @import url('https://fonts.cdnfonts.com/css/sf-pro-display');
          * { margin:0; padding:0; box-sizing:border-box; }
          body { font-family: ${SF}; background:#f5f5f3; }

          @keyframes adFadeIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }

          .ad-login {
            min-height:100vh; display:flex; align-items:center; justify-content:center;
            background:#f5f5f3; padding:24px;
          }
          .ad-login-card {
            width:380px; background:#fff; border:1px solid #ebebeb;
            border-radius:16px; padding:44px 36px;
            animation: adFadeIn .5s cubic-bezier(.4,0,.2,1) both;
          }
          .ad-login-top { display:flex; align-items:center; gap:12px; margin-bottom:28px; }
          .ad-login-logo { width:36px; height:36px; border-radius:10px; }
          .ad-login-brand { display:flex; flex-direction:column; }
          .ad-login-brand-name { font-size:16px; font-weight:700; color:#111; letter-spacing:-0.3px; }
          .ad-login-brand-tag { font-size:11px; color:#888; font-weight:500; }
          .ad-login-title { font-size:22px; font-weight:700; color:#111; margin-bottom:4px; letter-spacing:-0.5px; }
          .ad-login-sub { font-size:13px; color:#999; margin-bottom:28px; font-weight:400; }
          .ad-field { margin-bottom:16px; }
          .ad-field label { display:block; font-size:12px; font-weight:600; color:#555; margin-bottom:6px; }
          .ad-field-wrap {
            display:flex; align-items:center; background:#f8f8f6; border:1px solid #e8e8e6;
            border-radius:10px; padding:0 12px; transition:border-color .2s, box-shadow .2s;
          }
          .ad-field-wrap:focus-within { border-color:#22c55e; box-shadow:0 0 0 3px rgba(34,197,94,0.08); }
          .ad-field-wrap input {
            flex:1; background:none; border:none; outline:none; color:#111;
            font-size:14px; padding:11px 0; font-family:inherit;
          }
          .ad-field-wrap input::placeholder { color:#bbb; }
          .ad-btn-eye { background:none; border:none; color:#bbb; cursor:pointer; font-size:15px; padding:4px; transition:color .2s; }
          .ad-btn-eye:hover { color:#888; }
          .ad-btn-login {
            width:100%; padding:12px; background:#111; color:#fff;
            font-size:14px; font-weight:600; border:none; border-radius:10px;
            cursor:pointer; font-family:inherit; transition:background .2s, transform .1s;
            letter-spacing:-0.2px; margin-top:4px;
          }
          .ad-btn-login:hover { background:#333; }
          .ad-btn-login:active { transform:scale(0.98); }
          .ad-btn-login:disabled { opacity:.4; cursor:default; }
          .ad-error { color:#ef4444; font-size:13px; margin-bottom:12px; text-align:center; font-weight:500; }
        `}</style>
        <div className="ad-login">
          <form className="ad-login-card" onSubmit={handleLogin}>
            <div className="ad-login-top">
              <img src={LOGO_TALKU} alt="Talku" className="ad-login-logo" />
              <div className="ad-login-brand">
                <span className="ad-login-brand-name">Talku</span>
                <span className="ad-login-brand-tag">Platform Admin</span>
              </div>
            </div>
            <div className="ad-login-title">Iniciar sesion</div>
            <div className="ad-login-sub">Panel de administracion de la plataforma</div>
            {loginError && <div className="ad-error">{loginError}</div>}
            <div className="ad-field">
              <label>Email</label>
              <div className="ad-field-wrap">
                <input type="email" placeholder="admin@talku.online" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
              </div>
            </div>
            <div className="ad-field">
              <label>Contrasena</label>
              <div className="ad-field-wrap">
                <input type={showPass ? 'text' : 'password'} placeholder="Ingresa tu contrasena" value={password} onChange={e => setPassword(e.target.value)} required />
                <button type="button" className="ad-btn-eye" onClick={() => setShowPass(!showPass)}>
                  {showPass ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
            </div>
            <button type="submit" className="ad-btn-login" disabled={loginLoading}>
              {loginLoading ? 'Verificando...' : 'Ingresar'}
            </button>
          </form>
        </div>
      </>
    );
  }

  // ─── DASHBOARD ───
  const kpis = stats ? [
    { label: 'Total tenants', value: stats.total_tenants, icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
    )},
    { label: 'Tenants activos', value: stats.active_tenants, icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
    )},
    { label: 'Usuarios', value: stats.total_users, icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
    )},
    { label: 'Pedidos procesados', value: (stats.total_orders || 0).toLocaleString(), icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
    )},
    { label: 'Conversaciones', value: (stats.total_conversations || 0).toLocaleString(), icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
    )},
  ] : [];

  return (
    <>
      <style>{`
        @import url('https://fonts.cdnfonts.com/css/sf-pro-display');
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: ${SF}; background:#f5f5f3; color:#111; }

        @keyframes adFadeIn  { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes adSlideIn { from { transform:translateX(100%); } to { transform:translateX(0); } }
        @keyframes adFadeOverlay { from { opacity:0; } to { opacity:1; } }

        .ad { min-height:100vh; background:#f5f5f3; }

        /* Topbar */
        .ad-topbar {
          display:flex; align-items:center; justify-content:space-between;
          padding:0 32px; height:64px;
          background:#fff; border-bottom:1px solid #ebebeb;
          position:sticky; top:0; z-index:100;
        }
        .ad-topbar-left { display:flex; align-items:center; gap:12px; }
        .ad-topbar-logo { width:30px; height:30px; border-radius:8px; }
        .ad-topbar-name { font-size:15px; font-weight:700; color:#111; letter-spacing:-0.3px; }
        .ad-topbar-sep { width:1px; height:20px; background:#e8e8e6; margin:0 4px; }
        .ad-topbar-tag { font-size:12px; color:#888; font-weight:500; }
        .ad-topbar-right { display:flex; align-items:center; gap:14px; }
        .ad-topbar-user { font-size:13px; color:#888; font-weight:400; }
        .ad-topbar-logout {
          background:none; border:1px solid #e8e8e6; color:#888;
          font-size:12px; font-weight:500; padding:6px 14px; border-radius:8px;
          cursor:pointer; font-family:inherit; transition:all .2s;
        }
        .ad-topbar-logout:hover { border-color:#ccc; color:#111; }

        /* Body */
        .ad-body { max-width:1140px; margin:0 auto; padding:28px 24px; }

        /* Section header */
        .ad-section-hdr { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
        .ad-section-title { font-size:22px; font-weight:700; color:#111; letter-spacing:-0.5px; }
        .ad-section-sub { font-size:13px; color:#999; font-weight:400; }

        /* KPIs */
        .ad-kpis { display:grid; grid-template-columns:repeat(5,1fr); gap:12px; margin-bottom:28px; }
        @media (max-width:1000px) { .ad-kpis { grid-template-columns:repeat(3,1fr); } }
        @media (max-width:640px)  { .ad-kpis { grid-template-columns:repeat(2,1fr); } }
        .ad-kpi {
          background:#fff; border:1px solid #ebebeb; border-radius:14px;
          padding:18px 18px 16px; transition:box-shadow .2s, transform .2s;
          animation: adFadeIn .4s cubic-bezier(.4,0,.2,1) both;
        }
        .ad-kpi:nth-child(1) { animation-delay:.05s; }
        .ad-kpi:nth-child(2) { animation-delay:.1s; }
        .ad-kpi:nth-child(3) { animation-delay:.15s; }
        .ad-kpi:nth-child(4) { animation-delay:.2s; }
        .ad-kpi:nth-child(5) { animation-delay:.25s; }
        .ad-kpi:hover { box-shadow:0 4px 20px rgba(0,0,0,.04); transform:translateY(-1px); }
        .ad-kpi-icon { margin-bottom:12px; }
        .ad-kpi-val { font-size:26px; font-weight:700; color:#111; letter-spacing:-0.5px; line-height:1; margin-bottom:4px; }
        .ad-kpi-label { font-size:12px; color:#888; font-weight:500; }

        /* Table card */
        .ad-table-card {
          background:#fff; border:1px solid #ebebeb; border-radius:14px;
          overflow:hidden; animation: adFadeIn .4s ease .3s both;
        }
        .ad-table-header {
          display:flex; align-items:center; justify-content:space-between;
          padding:18px 22px; border-bottom:1px solid #f0f0ee;
        }
        .ad-table-title { font-size:15px; font-weight:700; color:#111; letter-spacing:-0.2px; }
        .ad-table-count { font-size:12px; color:#999; font-weight:500; background:#f5f5f3; padding:3px 10px; border-radius:999px; }
        .ad-table { width:100%; border-collapse:collapse; }
        .ad-table th {
          text-align:left; padding:11px 18px; font-size:11px; font-weight:600;
          color:#999; text-transform:uppercase; letter-spacing:0.4px;
          background:#fafaf8; border-bottom:1px solid #f0f0ee;
        }
        .ad-table td {
          padding:14px 18px; font-size:13px; color:#555; border-bottom:1px solid #f8f8f6;
          vertical-align:middle;
        }
        .ad-table tr:last-child td { border-bottom:none; }
        .ad-table tbody tr { transition:background .15s; cursor:pointer; }
        .ad-table tbody tr:hover td { background:#fafaf8; }

        /* Tenant name cell */
        .ad-t-name { display:flex; align-items:center; gap:10px; }
        .ad-t-avatar {
          width:34px; height:34px; border-radius:10px; background:#f0f0ee;
          display:flex; align-items:center; justify-content:center;
          font-size:14px; font-weight:700; color:#111; overflow:hidden; flex-shrink:0;
        }
        .ad-t-avatar img { width:100%; height:100%; object-fit:cover; }
        .ad-t-info { display:flex; flex-direction:column; }
        .ad-t-main { font-size:13px; font-weight:600; color:#111; }
        .ad-t-sub { font-size:11px; color:#bbb; margin-top:1px; }

        /* Badges */
        .ad-badge {
          display:inline-block; font-size:10px; font-weight:700; padding:3px 8px;
          border-radius:6px; text-transform:uppercase; letter-spacing:0.3px;
        }
        .ad-badge-status {
          display:inline-block; font-size:11px; font-weight:600; padding:3px 10px;
          border-radius:999px;
        }

        /* Integration dots */
        .ad-ints { display:flex; gap:5px; }
        .ad-int {
          font-size:10px; font-weight:600; padding:2px 7px; border-radius:5px;
          letter-spacing:0.2px;
        }

        /* Action buttons */
        .ad-actions { display:flex; gap:6px; }
        .ad-btn {
          font-size:11px; font-weight:600; padding:5px 12px; border-radius:8px;
          border:1px solid #e8e8e6; background:#fff; color:#555;
          cursor:pointer; font-family:inherit; transition:all .2s; white-space:nowrap;
        }
        .ad-btn:hover { border-color:#ccc; color:#111; }
        .ad-btn.danger { color:#ef4444; border-color:#fecaca; }
        .ad-btn.danger:hover { background:#fef2f2; border-color:#ef4444; }
        .ad-btn.success { color:#22c55e; border-color:#bbf7d0; }
        .ad-btn.success:hover { background:#f0fdf4; border-color:#22c55e; }
        .ad-btn:disabled { opacity:.4; cursor:default; }

        .ad-loading { text-align:center; padding:60px; color:#999; font-size:14px; }

        /* Detail overlay */
        .ad-overlay {
          position:fixed; inset:0; background:rgba(0,0,0,0.3);
          z-index:200; display:flex; justify-content:flex-end;
          animation: adFadeOverlay .2s ease both;
        }
        .ad-detail {
          width:520px; max-width:100%; height:100vh;
          background:#fff; border-left:1px solid #ebebeb;
          overflow-y:auto; animation: adSlideIn .25s cubic-bezier(.16,1,.3,1);
        }
        .ad-detail::-webkit-scrollbar { width:5px; }
        .ad-detail::-webkit-scrollbar-thumb { background:#ddd; border-radius:3px; }
        .ad-detail-hdr {
          padding:22px 24px; border-bottom:1px solid #f0f0ee;
          display:flex; align-items:center; justify-content:space-between;
          position:sticky; top:0; background:#fff; z-index:10;
        }
        .ad-detail-hdr-title { font-size:14px; font-weight:700; color:#111; }
        .ad-detail-close {
          background:none; border:none; color:#bbb; font-size:20px;
          cursor:pointer; padding:4px; line-height:1; transition:color .2s;
        }
        .ad-detail-close:hover { color:#111; }
        .ad-detail-body { padding:22px 24px; }

        /* Detail header */
        .ad-dh { display:flex; align-items:center; gap:14px; margin-bottom:20px; }
        .ad-dh-avatar {
          width:44px; height:44px; border-radius:12px; background:#f0f0ee;
          display:flex; align-items:center; justify-content:center;
          font-size:18px; font-weight:800; color:#111; overflow:hidden; flex-shrink:0;
        }
        .ad-dh-avatar img { width:100%; height:100%; object-fit:cover; }
        .ad-dh-name { font-size:18px; font-weight:700; color:#111; letter-spacing:-0.3px; }
        .ad-dh-slug { font-size:12px; color:#bbb; margin-top:1px; }
        .ad-dh-badges { display:flex; gap:6px; margin-top:10px; }

        /* Detail stats */
        .ad-dstats { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:24px; }
        .ad-dstat {
          background:#f8f8f6; border-radius:12px; padding:14px 16px;
        }
        .ad-dstat-val { font-size:20px; font-weight:700; color:#111; letter-spacing:-0.3px; }
        .ad-dstat-label { font-size:11px; color:#999; margin-top:1px; }

        /* Detail sections */
        .ad-dsec { margin-bottom:24px; }
        .ad-dsec-title { font-size:12px; font-weight:600; color:#999; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:10px; }
        .ad-dsec-list { display:flex; flex-direction:column; gap:6px; }
        .ad-dsec-item {
          background:#f8f8f6; border-radius:10px; padding:12px 14px;
          display:flex; align-items:center; justify-content:space-between;
        }
        .ad-dsec-item-left { display:flex; flex-direction:column; gap:1px; }
        .ad-dsec-item-name { font-size:13px; font-weight:600; color:#111; }
        .ad-dsec-item-sub { font-size:11px; color:#bbb; }
        .ad-dsec-empty { color:#ccc; font-size:12px; font-style:italic; padding:6px 0; }
      `}</style>

      <div className="ad">
        {/* TOPBAR */}
        <div className="ad-topbar">
          <div className="ad-topbar-left">
            <img src={LOGO_TALKU} alt="Talku" className="ad-topbar-logo" />
            <span className="ad-topbar-name">Talku</span>
            <div className="ad-topbar-sep" />
            <span className="ad-topbar-tag">Admin</span>
          </div>
          <div className="ad-topbar-right">
            <span className="ad-topbar-user">{adminUser.name || adminUser.email}</span>
            <button className="ad-topbar-logout" onClick={handleLogout}>Cerrar sesion</button>
          </div>
        </div>

        <div className="ad-body">
          {loading ? (
            <div className="ad-loading">Cargando datos de la plataforma...</div>
          ) : (
            <>
              {/* Header */}
              <div className="ad-section-hdr" style={{ marginBottom: 20 }}>
                <div>
                  <div className="ad-section-title">Panel de Administracion</div>
                  <div className="ad-section-sub">Vista general de la plataforma Talku</div>
                </div>
              </div>

              {/* KPIs */}
              <div className="ad-kpis">
                {kpis.map((k, i) => (
                  <div className="ad-kpi" key={i}>
                    <div className="ad-kpi-icon">{k.icon}</div>
                    <div className="ad-kpi-val">{k.value}</div>
                    <div className="ad-kpi-label">{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Tenant table */}
              <div className="ad-table-card">
                <div className="ad-table-header">
                  <span className="ad-table-title">Tenants</span>
                  <span className="ad-table-count">{tenants.length} registrados</span>
                </div>
                <table className="ad-table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Plan</th>
                      <th>Usuarios</th>
                      <th>Pedidos</th>
                      <th>Integraciones</th>
                      <th>Estado</th>
                      <th>Creado</th>
                      <th style={{ textAlign: 'right' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map(t => (
                      <tr key={t.id} onClick={() => openDetail(t)}>
                        <td>
                          <div className="ad-t-name">
                            <div className="ad-t-avatar">
                              {t.logo ? <img src={t.logo} alt="" /> : t.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="ad-t-info">
                              <span className="ad-t-main">{t.name}</span>
                              <span className="ad-t-sub">{t.owner_email || t.slug}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="ad-badge" style={{
                            color: PLAN_COLORS[t.plan] || '#888',
                            background: PLAN_BG[t.plan] || '#f5f5f3',
                          }}>{t.plan}</span>
                        </td>
                        <td style={{ fontWeight: 600, color: '#111' }}>{t.user_count}</td>
                        <td style={{ fontWeight: 600, color: '#111' }}>{(t.order_count || 0).toLocaleString()}</td>
                        <td>
                          <div className="ad-ints">
                            <span className="ad-int" style={{
                              color: t.integrations.shopify ? '#16a34a' : '#ccc',
                              background: t.integrations.shopify ? '#f0fdf4' : '#f8f8f6',
                            }}>Shopify</span>
                            <span className="ad-int" style={{
                              color: t.integrations.nexus ? '#2563eb' : '#ccc',
                              background: t.integrations.nexus ? '#eff6ff' : '#f8f8f6',
                            }}>Nexus</span>
                            <span className="ad-int" style={{
                              color: t.integrations.whatsapp ? '#7c3aed' : '#ccc',
                              background: t.integrations.whatsapp ? '#f5f3ff' : '#f8f8f6',
                            }}>WA</span>
                          </div>
                        </td>
                        <td>
                          <span className="ad-badge-status" style={{
                            color: t.active ? '#16a34a' : '#ef4444',
                            background: t.active ? '#f0fdf4' : '#fef2f2',
                          }}>{t.active ? 'Activo' : 'Inactivo'}</span>
                        </td>
                        <td style={{ fontSize: '12px', color: '#999' }}>{fmtDate(t.created_at)}</td>
                        <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                          <div className="ad-actions" style={{ justifyContent: 'flex-end' }}>
                            <button className="ad-btn" onClick={() => openDetail(t)}>Ver detalle</button>
                            <button
                              className={`ad-btn ${t.active ? 'danger' : 'success'}`}
                              onClick={() => toggleTenantActive(t)}
                              disabled={actionLoading === t.id}
                            >
                              {actionLoading === t.id ? '...' : (t.active ? 'Desactivar' : 'Activar')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {tenants.length === 0 && (
                      <tr><td colSpan={8} style={{ textAlign: 'center', color: '#ccc', padding: 40 }}>No hay tenants registrados</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* TENANT DETAIL PANEL */}
        {selectedTenant && (
          <div className="ad-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeDetail(); }}>
            <div className="ad-detail">
              <div className="ad-detail-hdr">
                <span className="ad-detail-hdr-title">Detalle del tenant</span>
                <button className="ad-detail-close" onClick={closeDetail}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              {detailLoading || !tenantDetail ? (
                <div className="ad-loading">Cargando...</div>
              ) : (
                <div className="ad-detail-body">
                  {/* Header */}
                  <div className="ad-dh">
                    <div className="ad-dh-avatar">
                      {tenantDetail.logo ? <img src={tenantDetail.logo} alt="" /> : tenantDetail.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="ad-dh-name">{tenantDetail.name}</div>
                      <div className="ad-dh-slug">{tenantDetail.slug}</div>
                    </div>
                  </div>
                  <div className="ad-dh-badges">
                    <span className="ad-badge" style={{
                      color: PLAN_COLORS[tenantDetail.plan] || '#888',
                      background: PLAN_BG[tenantDetail.plan] || '#f5f5f3',
                    }}>{tenantDetail.plan}</span>
                    <span className="ad-badge-status" style={{
                      color: tenantDetail.active ? '#16a34a' : '#ef4444',
                      background: tenantDetail.active ? '#f0fdf4' : '#fef2f2',
                    }}>{tenantDetail.active ? 'Activo' : 'Inactivo'}</span>
                  </div>

                  {/* Stats */}
                  <div className="ad-dstats" style={{ marginTop: 20 }}>
                    <div className="ad-dstat">
                      <div className="ad-dstat-val">{tenantDetail.members?.length || 0}</div>
                      <div className="ad-dstat-label">Miembros</div>
                    </div>
                    <div className="ad-dstat">
                      <div className="ad-dstat-val">{(tenantDetail.order_count || 0).toLocaleString()}</div>
                      <div className="ad-dstat-label">Pedidos</div>
                    </div>
                    <div className="ad-dstat">
                      <div className="ad-dstat-val">{(tenantDetail.conversation_count || 0).toLocaleString()}</div>
                      <div className="ad-dstat-label">Conversaciones</div>
                    </div>
                    <div className="ad-dstat">
                      <div className="ad-dstat-val" style={{ fontSize: 14 }}>{fmtDate(tenantDetail.created_at)}</div>
                      <div className="ad-dstat-label">Fecha creacion</div>
                    </div>
                  </div>

                  {/* Members */}
                  <div className="ad-dsec">
                    <div className="ad-dsec-title">Miembros ({tenantDetail.members?.length || 0})</div>
                    <div className="ad-dsec-list">
                      {tenantDetail.members?.length ? tenantDetail.members.map(m => (
                        <div className="ad-dsec-item" key={m.id}>
                          <div className="ad-dsec-item-left">
                            <div className="ad-dsec-item-name">{m.name || m.email}</div>
                            <div className="ad-dsec-item-sub">{m.email}{m.last_login ? ` · ${fmtDate(m.last_login)}` : ''}</div>
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <span className="ad-badge" style={{
                              color: m.role === 'owner' ? '#f59e0b' : m.role === 'admin' ? '#3b82f6' : '#888',
                              background: m.role === 'owner' ? '#fef3c7' : m.role === 'admin' ? '#dbeafe' : '#f5f5f3',
                            }}>{m.role}</span>
                            {!m.active && <span className="ad-badge" style={{ color: '#ef4444', background: '#fef2f2' }}>Inactivo</span>}
                          </div>
                        </div>
                      )) : <div className="ad-dsec-empty">Sin miembros</div>}
                    </div>
                  </div>

                  {/* Shopify */}
                  <div className="ad-dsec">
                    <div className="ad-dsec-title">Tiendas Shopify ({tenantDetail.shopify_stores?.length || 0})</div>
                    <div className="ad-dsec-list">
                      {tenantDetail.shopify_stores?.length ? tenantDetail.shopify_stores.map(s => (
                        <div className="ad-dsec-item" key={s.id}>
                          <div className="ad-dsec-item-left">
                            <div className="ad-dsec-item-name">{s.name}</div>
                            <div className="ad-dsec-item-sub">{s.domain}</div>
                          </div>
                          <span className="ad-badge" style={{
                            color: s.active ? '#16a34a' : '#ef4444',
                            background: s.active ? '#f0fdf4' : '#fef2f2',
                          }}>{s.active ? 'Activa' : 'Inactiva'}</span>
                        </div>
                      )) : <div className="ad-dsec-empty">Sin tiendas Shopify</div>}
                    </div>
                  </div>

                  {/* Nexus */}
                  <div className="ad-dsec">
                    <div className="ad-dsec-title">Cuentas Nexus ({tenantDetail.nexus_accounts?.length || 0})</div>
                    <div className="ad-dsec-list">
                      {tenantDetail.nexus_accounts?.length ? tenantDetail.nexus_accounts.map(n => (
                        <div className="ad-dsec-item" key={n.id}>
                          <div className="ad-dsec-item-left">
                            <div className="ad-dsec-item-name">{n.store_name}</div>
                            <div className="ad-dsec-item-sub">{n.nexus_base_url}</div>
                          </div>
                          <span className="ad-badge" style={{
                            color: n.is_active ? '#16a34a' : '#ef4444',
                            background: n.is_active ? '#f0fdf4' : '#fef2f2',
                          }}>{n.is_active ? 'Activa' : 'Inactiva'}</span>
                        </div>
                      )) : <div className="ad-dsec-empty">Sin cuentas Nexus</div>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
